"use strict";
/**
 * Coarse device/browser labels for the `UserEvent` table.
 *
 * Deliberately NOT `ua-parser-js`: the backend stores two short human-readable
 * strings, so a full UA database buys nothing, and that library has a history of
 * ReDoS advisories — not a risk worth taking on a code path that runs during
 * sign-in. This reducer is allowlist-based, input-capped, total (never throws),
 * and locked by a test matrix.
 *
 * Both fields are REQUIRED and non-empty on the backend DTO, so every branch
 * resolves to a real string; `Unknown` is the floor, never `''`.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.deriveNativeEnvironment = exports.NATIVE_APP_BROWSER_PREFIX = exports.deriveWebEnvironment = exports.UNKNOWN_ENVIRONMENT_VALUE = void 0;
/** ReDoS ceiling: no regex ever sees more than this many characters. */
const MAX_USER_AGENT_LENGTH = 512;
/** Backend `@MaxLength(255)` on both columns. */
const MAX_FIELD_LENGTH = 255;
exports.UNKNOWN_ENVIRONMENT_VALUE = 'Unknown';
const clamp = (value) => value.length > MAX_FIELD_LENGTH ? value.slice(0, MAX_FIELD_LENGTH) : value;
/**
 * Device family from a UA string.
 *
 * ORDER IS LOAD-BEARING for the iOS family: an iPod touch reports
 * `(iPod touch; CPU iPhone OS 17_3 …)` and an iPad has historically reported
 * `CPU iPhone OS` too — both contain the `iphone` token. So the narrower
 * device tokens must be tested BEFORE `iphone`, or every iPod/iPad is
 * misreported as an iPhone. Likewise `Macintosh` appears in iPadOS 13+ desktop
 * -mode UAs, so the Mac branch sits after the iPad branch.
 */
const deriveDevice = (ua) => {
    if (/\bipod\b/.test(ua))
        return 'iPod';
    if (/\bipad\b/.test(ua))
        return 'iPad';
    if (/\biphone\b/.test(ua))
        return 'iPhone';
    if (/\bandroid\b/.test(ua))
        return 'Android';
    if (/\bcros\b/.test(ua))
        return 'ChromeOS';
    if (/\bwindows\b|\bwin32\b|\bwin64\b/.test(ua))
        return 'Windows';
    if (/\bmacintosh\b|\bmac os x\b/.test(ua))
        return 'Mac';
    if (/\blinux\b/.test(ua))
        return 'Linux';
    return exports.UNKNOWN_ENVIRONMENT_VALUE;
};
/**
 * Browser family from a UA string.
 *
 * ORDER IS LOAD-BEARING. Every Chromium browser claims `Chrome` AND `Safari`
 * in its UA, and Chrome itself claims `Safari`. So the specific forks must be
 * tested first, then Chrome, then Safari last:
 *   Edge (`edg/`) → Opera (`opr/`|`opera`) → Samsung (`samsungbrowser`)
 *   → Firefox (`firefox`|`fxios`) → Chrome (`chrome`|`crios`|`chromium`)
 *   → Safari (`safari`)
 * `fxios`/`crios` are the iOS variants, which carry no `firefox`/`chrome` token.
 */
const deriveBrowser = (ua) => {
    if (/\bedg(?:e|a|ios)?\//.test(ua))
        return 'Edge';
    if (/\bopr\/|\bopera\b/.test(ua))
        return 'Opera';
    if (/samsungbrowser/.test(ua))
        return 'Samsung Internet';
    if (/firefox\/|fxios\//.test(ua))
        return 'Firefox';
    if (/chrome\/|crios\/|chromium\//.test(ua))
        return 'Chrome';
    if (/safari\//.test(ua))
        return 'Safari';
    return exports.UNKNOWN_ENVIRONMENT_VALUE;
};
/**
 * Derive `{ device, browser }` from a browser User-Agent string.
 *
 * Total: any input — undefined, empty, binary garbage, a megabyte of text —
 * yields a valid, non-empty, length-capped pair.
 */
const deriveWebEnvironment = (userAgent) => {
    try {
        if (typeof userAgent !== 'string' || userAgent.length === 0) {
            return {
                device: exports.UNKNOWN_ENVIRONMENT_VALUE,
                browser: exports.UNKNOWN_ENVIRONMENT_VALUE,
            };
        }
        const ua = userAgent.slice(0, MAX_USER_AGENT_LENGTH).toLowerCase();
        return {
            device: clamp(deriveDevice(ua)),
            browser: clamp(deriveBrowser(ua)),
        };
    }
    catch {
        return {
            device: exports.UNKNOWN_ENVIRONMENT_VALUE,
            browser: exports.UNKNOWN_ENVIRONMENT_VALUE,
        };
    }
};
exports.deriveWebEnvironment = deriveWebEnvironment;
/**
 * Native-app equivalent. A React Native app has no browser, but the backend
 * requires a non-empty `browser` string — so we send the app identifier and
 * version there. That makes the column do double duty as an app-version
 * breadcrumb in the events table, which is genuinely useful for support.
 *
 * Convention (keep web + mobile from drifting):
 *   device  → the OS/model label, e.g. `iPhone`, `Pixel 7`, `Android`
 *   browser → `SkimApp/<semver>`
 */
exports.NATIVE_APP_BROWSER_PREFIX = 'SkimApp';
const deriveNativeEnvironment = (input) => {
    try {
        const model = typeof input.model === 'string' && input.model.trim().length > 0
            ? input.model.trim()
            : null;
        let device = model;
        if (!device) {
            if (input.os === 'ios')
                device = 'iPhone';
            else if (input.os === 'android')
                device = 'Android';
            else if (typeof input.os === 'string' && input.os.length > 0)
                device = input.os;
            else
                device = exports.UNKNOWN_ENVIRONMENT_VALUE;
        }
        const version = typeof input.appVersion === 'string' && input.appVersion.length > 0
            ? input.appVersion
            : '0.0.0';
        return {
            device: clamp(device),
            browser: clamp(`${exports.NATIVE_APP_BROWSER_PREFIX}/${version}`),
        };
    }
    catch {
        return {
            device: exports.UNKNOWN_ENVIRONMENT_VALUE,
            browser: exports.NATIVE_APP_BROWSER_PREFIX,
        };
    }
};
exports.deriveNativeEnvironment = deriveNativeEnvironment;
